/**
 * VectorGraphic Component
 * 
 * React component for the VectorGraphic TipTap node.
 * Provides toggle between rendered diagram view and code editor view.
 * Uses mermaid.js for client-side rendering.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import debounce from 'lodash/debounce';
import { useConfirm } from '@/components/ui';
import { 
  MERMAID_TEMPLATES, 
  MERMAID_TYPE_CONFIG, 
  MERMAID_DIAGRAM_TYPES 
} from '../../config/mermaid-templates';
import { logger } from '../../utils/logger';
import type { MermaidDiagramType } from '../../types/panel-types';

// Lazy load mermaid to avoid 800KB bundle on initial load
let mermaidInstance: typeof import('mermaid').default | null = null;
let mermaidLoadPromise: Promise<typeof import('mermaid').default> | null = null;

const loadMermaid = async () => {
  if (mermaidInstance) return mermaidInstance;
  if (mermaidLoadPromise) return mermaidLoadPromise;
  
  mermaidLoadPromise = (async () => {
    const mermaid = await import('mermaid');
    mermaid.default.initialize({ 
      startOnLoad: false, 
      theme: 'default',
      // Use high-contrast colors that work on light backgrounds (matching PDF output)
      themeVariables: {
        // General colors
        primaryColor: '#3b82f6',
        primaryTextColor: '#1e293b',
        primaryBorderColor: '#2563eb',
        lineColor: '#64748b',
        secondaryColor: '#f1f5f9',
        tertiaryColor: '#e2e8f0',
        // Text colors - ensure visibility
        textColor: '#1e293b',
        mainBkg: '#ffffff',
        // Gantt specific
        titleColor: '#1e293b',
        sectionBkgColor: '#f1f5f9',
        altSectionBkgColor: '#e2e8f0',
        gridColor: '#94a3b8',
        todayLineColor: '#ef4444',
        taskTextColor: '#1e293b',
        taskTextDarkColor: '#1e293b',
        taskTextOutsideColor: '#1e293b',
        taskBorderColor: '#3b82f6',
        activeTaskBorderColor: '#2563eb',
        doneTaskBkgColor: '#86efac',
        doneTaskBorderColor: '#22c55e',
        critBorderColor: '#ef4444',
        critBkgColor: '#fecaca',
      },
    });
    mermaidInstance = mermaid.default;
    return mermaid.default;
  })();
  
  return mermaidLoadPromise;
};

export const VectorGraphicComponent: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  selected,
}) => {
  const confirm = useConfirm();
  const [viewMode, setViewMode] = useState<'rendered' | 'code'>('rendered');
  const [editingDsl, setEditingDsl] = useState(node.attrs.content || '');
  const [renderedSvg, setRenderedSvg] = useState<string | null>(null);
  const [syntaxError, setSyntaxError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCaption, setEditingCaption] = useState(node.attrs.caption || '');
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const renderIdRef = useRef(0);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  
  // Check if content is still a default template (not modified by user)
  const isDefaultTemplate = useMemo(() => {
    const content = (node.attrs.content || '').trim();
    return Object.values(MERMAID_TEMPLATES).some(
      template => template.trim() === content
    );
  }, [node.attrs.content]);
  
  // Handle template selection
  const handleTemplateSelect = useCallback(async (templateType: MermaidDiagramType) => {
    const newContent = MERMAID_TEMPLATES[templateType];
    
    // If content has been modified (not a default template), confirm before replacing
    if (!isDefaultTemplate && node.attrs.content?.trim()) {
      const ok = await confirm({
        title: 'Replace Diagram Content?',
        description: 'This will replace your current diagram with a new template. Your existing content will be lost.',
        confirmText: 'Replace',
        cancelText: 'Cancel',
      });
      if (!ok) {
        setShowTemplateDropdown(false);
        return;
      }
    }
    
    updateAttributes({ content: newContent });
    setEditingDsl(newContent);
    setShowTemplateDropdown(false);
  }, [isDefaultTemplate, node.attrs.content, confirm, updateAttributes]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
        setShowTemplateDropdown(false);
      }
    };
    
    if (showTemplateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTemplateDropdown]);
  
  // Generate unique ID for this diagram
  const diagramId = useMemo(() => 
    `diagram-${node.attrs['data-block-id'] || Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    [node.attrs['data-block-id']]
  );

  // Render diagram function
  const renderDiagram = useCallback(async (dsl: string) => {
    if (!dsl?.trim()) {
      setRenderedSvg(null);
      setSyntaxError(null);
      setIsLoading(false);
      return;
    }
    
    const currentRenderId = ++renderIdRef.current;
    
    try {
      const mermaid = await loadMermaid();
      
      // Skip if a newer render was triggered
      if (currentRenderId !== renderIdRef.current) return;
      
      // Validate syntax first
      await mermaid.parse(dsl);
      setSyntaxError(null);
      
      // Render to SVG
      const { svg } = await mermaid.render(`${diagramId}-${currentRenderId}`, dsl);
      
      // Only update if this is still the latest render
      if (currentRenderId === renderIdRef.current) {
        // Post-process SVG to use responsive width
        // Replace fixed width/height attributes with 100% width
        const responsiveSvg = svg
          .replace(/(<svg[^>]*)\s+width="[^"]*"/i, '$1 width="100%"')
          .replace(/(<svg[^>]*)\s+height="[^"]*"/i, '$1')
          .replace(/(<svg[^>]*)>/i, '$1 style="width: 100%; height: auto;">');
        
        setRenderedSvg(responsiveSvg);
        setIsLoading(false);
      }
    } catch (error: unknown) {
      if (currentRenderId === renderIdRef.current) {
        const errorMessage = error instanceof Error ? error.message : 'Mermaid syntax error';
        setSyntaxError(errorMessage);
        setRenderedSvg(null);
        setIsLoading(false);
      }
    }
  }, [diagramId]);

  // Debounced preview render (500ms delay)
  const debouncedRender = useMemo(
    () => debounce((dsl: string) => renderDiagram(dsl), 500),
    [renderDiagram]
  );

  // Render on mount and content changes
  useEffect(() => {
    if (node.attrs.content) {
      setIsLoading(true);
      renderDiagram(node.attrs.content);
    } else {
      setIsLoading(false);
    }
  }, [node.attrs.content, renderDiagram]);

  // Update editing state when node content changes externally
  useEffect(() => {
    if (viewMode === 'rendered') {
      setEditingDsl(node.attrs.content || '');
    }
  }, [node.attrs.content, viewMode]);

  // Handle save from code view
  const handleSave = useCallback(() => {
    const MAX_DSL_LENGTH = 50000;
    const WARN_DSL_LENGTH = 10000;
    
    if (editingDsl.length > MAX_DSL_LENGTH) {
      setSyntaxError('Diagram source exceeds maximum size (50KB)');
      return;
    }
    
    if (editingDsl.length > WARN_DSL_LENGTH) {
      logger.warn('[VectorGraphic] Large diagram may render slowly');
    }
    
    // Validate before saving
    loadMermaid().then(mermaid => {
      mermaid.parse(editingDsl)
        .then(() => {
          updateAttributes({ 
            content: editingDsl,
            caption: editingCaption || null,
          });
          setViewMode('rendered');
          setSyntaxError(null);
        })
        .catch((error: Error) => {
          setSyntaxError(error.message);
        });
    });
  }, [editingDsl, editingCaption, updateAttributes]);

  // Handle cancel from code view
  const handleCancel = useCallback(() => {
    setEditingDsl(node.attrs.content || '');
    setEditingCaption(node.attrs.caption || '');
    setSyntaxError(null);
    setViewMode('rendered');
  }, [node.attrs.content, node.attrs.caption]);

  // Handle delete - removes from document only, not from project
  const handleDelete = useCallback(async () => {
    const ok = await confirm({
      title: 'Remove Diagram from Document',
      description: 'This will remove the diagram from this document only. The original project diagram (if any) will not be affected.',
      confirmText: 'Remove',
      cancelText: 'Cancel',
    });
    if (ok) {
      deleteNode();
    }
  }, [deleteNode, confirm]);

  const blockId = node.attrs['data-block-id'];
  
  return (
    <NodeViewWrapper 
      className={`vector-graphic-wrapper ${selected ? 'selected' : ''}`}
      data-type="vector-graphic"
      data-block-id={blockId}
    >
      {/* Toolbar */}
      <div className="vector-graphic-toolbar">
        <div className="toggle-buttons">
          <button 
            className={`toggle-btn ${viewMode === 'rendered' ? 'active' : ''}`}
            onClick={() => setViewMode('rendered')}
            title="View rendered diagram"
          >
            <span className="icon">📊</span> Rendered
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'code' ? 'active' : ''}`}
            onClick={() => setViewMode('code')}
            title="Edit Mermaid code"
          >
            <span className="icon">&lt;/&gt;</span> Code
          </button>
        </div>
        
        {/* Block ID badge and Template dropdown - only shown when selected */}
        {selected && (
          <>
            {/* Block ID badge */}
            {blockId && (
              <div 
                className="block-id-badge"
                onClick={() => {
                  navigator.clipboard.writeText(blockId);
                }}
                title={`Block ID: ${blockId} (click to copy)`}
              >
                <span className="block-id-symbol">§</span>
                <span className="block-id-text">{blockId}</span>
              </div>
            )}
            
            {/* Template dropdown */}
            <div className="template-dropdown-container" ref={templateDropdownRef}>
              <button
                className="template-btn"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                title="Change diagram type"
              >
                <span className="icon">📐</span> Templates
                <span className="dropdown-arrow">▼</span>
              </button>
              
              {showTemplateDropdown && (
                <div className="template-dropdown">
                  <div className="template-dropdown-header">Diagram Templates</div>
                  {MERMAID_DIAGRAM_TYPES.map((type) => {
                    const config = MERMAID_TYPE_CONFIG[type];
                    return (
                      <button
                        key={type}
                        className="template-option"
                        onClick={() => handleTemplateSelect(type)}
                      >
                        <span className="template-icon">{config.icon}</span>
                        <span className="template-label">{config.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
        
        <div className="action-buttons">
          <button 
            className="delete-btn"
            onClick={handleDelete}
            title="Delete diagram"
          >
            🗑
          </button>
        </div>
      </div>

      {/* Content area */}
      {viewMode === 'rendered' ? (
        <div className="diagram-rendered">
          {isLoading ? (
            <div className="diagram-loading">
              <div className="spinner"></div>
              <span>Loading diagram...</span>
            </div>
          ) : renderedSvg ? (
            <div 
              className="diagram-svg-container"
              dangerouslySetInnerHTML={{ __html: renderedSvg }} 
            />
          ) : syntaxError ? (
            <div className="diagram-error">
              <div className="error-icon">⚠️</div>
              <div className="error-message">
                <strong>Diagram Error</strong>
                <pre>{syntaxError}</pre>
              </div>
              <button 
                className="edit-btn"
                onClick={() => setViewMode('code')}
              >
                Edit Code
              </button>
            </div>
          ) : (
            <div className="diagram-empty">
              <div className="empty-icon">📊</div>
              <span>No diagram content</span>
              <button 
                className="edit-btn"
                onClick={() => setViewMode('code')}
              >
                Add Diagram
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="diagram-code-editor">
          <textarea
            value={editingDsl}
            onChange={(e) => {
              setEditingDsl(e.target.value);
              debouncedRender(e.target.value);
            }}
            placeholder="Enter Mermaid diagram code...

Example:
flowchart TD
  A[Start] --> B[Process]
  B --> C[End]"
            spellCheck={false}
          />
          
          {syntaxError && (
            <div className="syntax-error">
              <span className="error-icon">⚠️</span>
              {syntaxError}
            </div>
          )}
          
          <div className="caption-input">
            <label>Caption:</label>
            <input
              type="text"
              value={editingCaption}
              onChange={(e) => setEditingCaption(e.target.value)}
              placeholder="Figure caption (optional)"
            />
          </div>
          
          <div className="editor-actions">
            <button 
              className="cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button 
              className="save-btn"
              onClick={handleSave}
              disabled={!!syntaxError}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Caption (in rendered view) */}
      {viewMode === 'rendered' && (node.attrs.figureNumber || node.attrs.caption) && (
        <div className="diagram-caption">
          {node.attrs.figureNumber && (
            <span className="figure-number">{node.attrs.figureNumber}: </span>
          )}
          {node.attrs.caption}
        </div>
      )}
    </NodeViewWrapper>
  );
};
